import { LocalDate } from "@js-joda/core";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import { getStockholmLocalDateNow } from "components/designV2/datePicker/datePicker.utils";
import { Modal } from "components/designV2/modal/modal";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Api } from "services/api/api.service";
import {
  closePendingPdfPreview,
  openPendingPdfPreview,
  previewPdfAndDownload,
} from "utils/pdfPreview";
import {
  formatLocalDateAsApiDate,
  formatLocalDateWithWeekdayLabel,
} from "web-apps/company/utils/timeAndDate";
import {
  Body,
  DatePickerWrap,
  ErrorText,
  FieldBlock,
  FieldInput,
  FieldLabel,
  FooterRow,
  HiddenRadio,
  RadioControl,
  RadioDescription,
  RadioGroup,
  RadioLabel,
  RadioOption,
  SectionTitle,
} from "./stampClockStaffRegisterExportModal.styled";

type ExportMode = "today" | "range";

interface StampClockStaffRegisterExportModalProps {
  companyId: number | undefined;
  isOpen: boolean;
  onClose: () => void;
}

const SSN_REGEX = /^\d{12}$/;

const isValidSsn = (value: string): boolean => SSN_REGEX.test(value.trim());

const toRangeFromDates = (
  dates: LocalDate[],
): { start: LocalDate; end: LocalDate } | null => {
  if (dates.length < 2) return null;
  const sortedDates = [...dates].sort((a, b) => a.compareTo(b));
  const first = sortedDates[0];
  const last = sortedDates[sortedDates.length - 1];
  if (!first || !last) return null;
  return { start: first, end: last };
};

const resolveFileName = (fromDate: string, toDate: string): string => {
  if (fromDate === toDate) {
    return `staff-register-${fromDate}.pdf`;
  }
  return `staff-register-${fromDate}-${toDate}.pdf`;
};

export const StampClockStaffRegisterExportModal = ({
  companyId,
  isOpen,
  onClose,
}: StampClockStaffRegisterExportModalProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const [mode, setMode] = useState<ExportMode>("today");
  const [selectedRange, setSelectedRange] = useState<LocalDate[]>([]);
  const [ssnKey, setSsnKey] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const today = getStockholmLocalDateNow();
  const todayLabel = useMemo(
    () => formatLocalDateWithWeekdayLabel(today, i18n.language),
    [i18n.language, today],
  );

  const range = useMemo(() => toRangeFromDates(selectedRange), [selectedRange]);
  const hasValidSsn = isValidSsn(ssnKey);
  const canExport =
    Boolean(companyId) &&
    hasValidSsn &&
    (mode === "today" || (mode === "range" && range != null));

  const resetState = (): void => {
    setMode("today");
    setSelectedRange([]);
    setSsnKey("");
    setExportError(null);
    setIsExporting(false);
  };

  const handleClose = (): void => {
    resetState();
    onClose();
  };

  const handleExport = async (): Promise<void> => {
    if (!companyId) return;
    if (!hasValidSsn) {
      setExportError(t("ProfileCompany.EnterSsn"));
      return;
    }
    if (mode === "range" && !range) return;

    const fromDate =
      mode === "today"
        ? formatLocalDateAsApiDate(today)
        : range
          ? formatLocalDateAsApiDate(range.start)
          : null;
    const toDate =
      mode === "today"
        ? formatLocalDateAsApiDate(today)
        : range
          ? formatLocalDateAsApiDate(range.end)
          : null;
    if (!fromDate || !toDate) return;

    setExportError(null);
    setIsExporting(true);
    const pendingPreview = openPendingPdfPreview(
      t("TimeReports.StaffRegister"),
      t("TimeReports.LoadingStaffRegister"),
    );

    try {
      const response = await Api().company.timeReports.exportStaffRegister(
        companyId,
        fromDate,
        toDate,
        ssnKey.trim(),
      );
      if (response.status === 204) {
        closePendingPdfPreview(pendingPreview);
        setExportError(t("TimeReports.ExportNoData"));
        return;
      }
      previewPdfAndDownload(
        pendingPreview,
        response.data,
        resolveFileName(fromDate, toDate),
      );
      handleClose();
    } catch {
      closePendingPdfPreview(pendingPreview);
      setExportError(t("RegisterCompany.SomethingWentWrong"));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Modal
      label={t("TimeReports.StaffRegister")}
      isOpen={isOpen}
      onClose={handleClose}
      showCloseButton={false}
      size="compact"
      footer={
        <FooterRow>
          <SecondaryButton
            label={t("DesignComponents.Cancel")}
            size="small"
            onClick={handleClose}
            disabled={isExporting}
          />
          <PrimaryButton
            label={t("TimeReports.GeneratePdf")}
            iconName="v2/download"
            size="small"
            onClick={handleExport}
            disabled={!canExport}
            isLoading={isExporting}
          />
        </FooterRow>
      }
    >
      <Body>
        <SectionTitle>{t("TimeReports.ExportPeriodTitle")}</SectionTitle>
        <RadioGroup
          role="radiogroup"
          aria-label={t("TimeReports.ExportPeriodTitle")}
        >
          <RadioOption>
            <HiddenRadio
              type="radio"
              name="stamp-clock-export-period"
              checked={mode === "today"}
              onChange={() => setMode("today")}
            />
            <RadioControl />
            <RadioLabel>{t("TimeReports.ExportPeriodDay")}</RadioLabel>
            <RadioDescription>{todayLabel}</RadioDescription>
          </RadioOption>
          <RadioOption>
            <HiddenRadio
              type="radio"
              name="stamp-clock-export-period"
              checked={mode === "range"}
              onChange={() => setMode("range")}
            />
            <RadioControl />
            <RadioLabel>{t("DatePicker.Range")}</RadioLabel>
          </RadioOption>
        </RadioGroup>

        {mode === "range" ? (
          <DatePickerWrap>
            <DatePicker
              label={t("TimeReports.Date")}
              allowRangePicker
              rangeOnly
              allowClear
              value={selectedRange[0] ?? null}
              onRangeChange={(dates) => setSelectedRange(dates)}
            />
          </DatePickerWrap>
        ) : null}

        <FieldBlock>
          <FieldLabel htmlFor="stamp-clock-staff-register-ssn">
            {t("ProfileCompany.PersonalNumberLabel")}
          </FieldLabel>
          <FieldInput
            id="stamp-clock-staff-register-ssn"
            value={ssnKey}
            onChange={(event) => {
              const next = event.target.value.replace(/\D/g, "").slice(0, 12);
              setSsnKey(next);
              if (exportError) setExportError(null);
            }}
            placeholder={t("ProfileCompany.PersonalNumberPlaceholder")}
            inputMode="numeric"
            autoComplete="off"
            $hasError={ssnKey.length > 0 && !hasValidSsn}
          />
        </FieldBlock>

        {exportError ? <ErrorText>{exportError}</ErrorText> : null}
      </Body>
    </Modal>
  );
};
